import { Link, Stack } from '@mui/material';

import Iconify from 'src/components/iconify';
import { CasBaseDialog, CasDocumentationField } from 'src/compass/shared/components';

interface CasEnvironmentDialogProps {
  open: boolean;
  onClose: VoidFunction;
  /** The environment's name - the same label the header tag carries. */
  title: string;
  documentation: string;
  /** Absolute URL of the environment's .docx attachment, or '' when it has none. */
  attachmentUrl: string;
  /** The name that download saves as - the link's label. */
  attachmentFilename: string;
}

/**
 * The notes for the environment this site is running against, opened from the
 * header tag. Both values come from Public/Properties/Site; either one alone is
 * reason enough to show the dialog, so the Documentation field can be absent
 * (CasDocumentationField renders nothing for an empty value) and the attachment
 * link can be absent, but not both - the header only makes the tag clickable
 * when there is something here.
 */
export default function CasEnvironmentDialog({
  open,
  onClose,
  title,
  documentation,
  attachmentUrl,
  attachmentFilename,
}: CasEnvironmentDialogProps) {
  return (
    <CasBaseDialog open={open} onClose={onClose} title={title} maxWidth="sm" showCloseIcon>
      <Stack spacing={2}>
        {/* Documentation is nvarchar(max) that people paste runbook content
            into, so its line breaks have to survive - CasDisplayField renders
            with white-space:normal, which collapses them. Same pre-wrap the
            Environments tab's own Documentation column uses.

            The emptiness check is ours because wrapping the text in an element
            defeats CasDocumentationField's own blank-value guard: an
            environment with an attachment but no documentation must show the
            link alone, not an empty labelled field. */}
        {documentation.trim() !== '' && (
          <CasDocumentationField
            value={<span style={{ whiteSpace: 'pre-wrap' }}>{documentation}</span>}
            fullWidth
          />
        )}
        {attachmentUrl && (
          <Link
            href={attachmentUrl}
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
          >
            <Iconify width="20px" height="20px" icon="vaadin:file-text-o" />
            {/* The filename the download actually saves as, resolved by the
                API, so the link and the saved file read the same. */}
            {attachmentFilename}
          </Link>
        )}
      </Stack>
    </CasBaseDialog>
  );
}
